import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { id: "pendiente", label: "Pendiente", color: "pill-gray" },
  { id: "en_proceso", label: "En proceso", color: "pill-blue" },
  { id: "revision", label: "Revisión", color: "pill-yellow" },
  { id: "bloqueada", label: "Bloqueada", color: "pill-red" },
  { id: "terminada", label: "Terminada", color: "pill-green" },
];

const PRIORITY_COLORS = {
  baja: "pill-gray",
  media: "pill-blue",
  alta: "pill-yellow",
  urgente: "pill-red",
};

const Tasks = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/tasks").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDrop = async (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    await api.patch(`/tasks/${id}`, { status });
    load();
  };

  return (
    <Layout
      title="Tareas"
      subtitle="Tablero de producción"
      actions={
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-task-btn">
          <Plus size={14} /> Nueva tarea
        </button>
      }
    >
      {loading ? (
        <div className="flex justify-center py-20"><span className="spinner" /></div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="tasks-kanban">
          {COLUMNS.map((col) => {
            const colItems = items.filter((t) => t.status === col.id);
            return (
              <div
                key={col.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => onDrop(e, col.id)}
                className="kanban-col"
                data-testid={`col-${col.id}`}
              >
                <div className="flex justify-between items-center mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className={`pill ${col.color} text-[10px]`}>{col.label}</span>
                    <span className="text-xs text-[#666]">{colItems.length}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {colItems.map((t) => (
                    <TaskCard key={t.id} task={t} onChange={load} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <TaskForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
    </Layout>
  );
};

const TaskCard = ({ task, onChange }) => {
  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar?")) return;
    await api.delete(`/tasks/${task.id}`);
    onChange();
  };
  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
      className="kanban-card group"
      data-testid={`task-${task.id}`}
    >
      <div className="flex justify-between items-start mb-2">
        <span className={`pill ${PRIORITY_COLORS[task.priority] || "pill-gray"} text-[10px]`}>{task.priority}</span>
        <button onClick={remove} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-600"><Trash2 size={12} /></button>
      </div>
      <div className="text-sm font-medium text-[#0A0A0A] mb-1">{task.title}</div>
      {task.description && <div className="text-xs text-[#666] line-clamp-2">{task.description}</div>}
      <div className="flex justify-between items-center mt-2 text-xs text-[#666]">
        <span>{task.assignee_name || "Sin asignar"}</span>
        {task.due_date && <span>{new Date(task.due_date).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}</span>}
      </div>
      {task.tags?.length > 0 && (
        <div className="flex gap-1 mt-2 flex-wrap">
          {task.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="pill pill-gray text-[10px]">{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const TaskForm = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: "", description: "", status: "pendiente", priority: "media",
    category: "general", assignee_name: "", due_date: "", tags: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        due_date: form.due_date || null,
      };
      await api.post("/tasks", body);
      toast.success("Tarea creada");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-7 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <div className="label-overline">Crear</div>
            <h2 className="font-display text-2xl font-medium">Nueva tarea</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div>
          <label className="aura-label">Título *</label>
          <input required className="aura-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
        </div>
        <div>
          <label className="aura-label">Descripción</label>
          <textarea className="aura-input" rows="3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="aura-label">Estado</label>
            <select className="aura-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Prioridad</label>
            <select className="aura-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="baja">Baja</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="urgente">Urgente</option>
            </select>
          </div>
          <div>
            <label className="aura-label">Categoría</label>
            <select className="aura-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {["general", "diseno", "video", "copy", "ugc", "sucursal", "web", "pauta", "reunion"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="aura-label">Asignar a</label>
            <input className="aura-input" value={form.assignee_name} onChange={(e) => setForm({ ...form, assignee_name: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Fecha límite</label>
            <input type="date" className="aura-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <label className="aura-label">Tags (separadas por coma)</label>
            <input className="aura-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="reel, promo" />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center flex items-center gap-2" data-testid="task-submit">
            {saving && <span className="spinner" />} Crear tarea
          </button>
        </div>
      </form>
    </div>
  );
};

export default Tasks;
